import { formatInstruction } from '../maneuvers/instructions';
import { roadbookLandmarkLabels } from '../app/accessibility-labels';
import type { LanguageCode, RouteInstruction, UnitSystem } from '../types';

export interface RoadbookProps {
  label?: string;
  instructions: RouteInstruction[];
  activeIndex: number | null;
  language: LanguageCode;
  units: UnitSystem;
}

const unavailable: Record<LanguageCode, string> = {
  EN: 'Maneuvers unavailable', DE: 'Fahrhinweise nicht verfügbar', ES: 'Maniobras no disponibles',
  IT: 'Manovre non disponibili', FR: 'Manœuvres indisponibles'
};
const viaLabel: Record<LanguageCode, string> = { EN: 'Via', DE: 'Zwischenziel', ES: 'Vía', IT: 'Tappa', FR: 'Étape' };
const finishLabel: Record<LanguageCode, string> = { EN: 'Finish', DE: 'Ziel', ES: 'Destino', IT: 'Arrivo', FR: 'Arrivée' };

function distanceLabel(meters: number, units: UnitSystem): string {
  if (units === 'imperial') return meters < 1_609.344 ? `${Math.round(meters * 3.28084)} ft` : `${(meters / 1_609.344).toFixed(1)} mi`;
  return meters < 1_000 ? `${Math.round(meters)} m` : `${(meters / 1_000).toFixed(1)} km`;
}

export function Roadbook({ language, label = roadbookLandmarkLabels[language], instructions, activeIndex, units }: RoadbookProps) {
  if (instructions.length === 0) return <section className="roadbook roadbook--unavailable" aria-label={label}><h3>{unavailable[language]}</h3></section>;

  return (
    <section className="roadbook" aria-label={label}>
      <ol>
        {instructions.map((instruction, index) => {
          const kind = instruction.sign === 5 ? 'via' : instruction.sign === 4 ? 'finish' : 'maneuver';
          const prefix = kind === 'via' ? viaLabel[language] : kind === 'finish' ? finishLabel[language] : null;
          return (
            <li key={`${instruction.interval[0]}-${instruction.interval[1]}-${index}`} aria-current={index === activeIndex ? 'step' : undefined} data-testid={kind === 'maneuver' ? undefined : `roadbook-${kind}`}>
              <span className="roadbook__index" aria-hidden="true">{index + 1}</span>
              <div><strong>{prefix ? `${prefix} · ${instruction.street_name || formatInstruction(instruction, language)}` : formatInstruction(instruction, language)}</strong>{instruction.distance_m > 0 ? <small>{distanceLabel(instruction.distance_m, units)}</small> : null}</div>
            </li>
          );
        })}
      </ol>
    </section>
  );
}
